import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "textarea", "submit"]

  submit(event) {
    event.preventDefault()

    const form = event.currentTarget
    const formData = new FormData(form)
    const originalButtonText = this.submitTarget.value

    // Disable submit button
    this.submitTarget.disabled = true
    this.submitTarget.value = 'Adding...'

    // The meta tag is absent wherever forgery protection is off, and reading
    // .content off null throws before fetch is called — the request never
    // happens and no .catch runs. See spec/views/admin_fetch_csrf_guard_spec.
    const csrf = document.querySelector('meta[name="csrf-token"]')

    fetch(form.action, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': csrf ? csrf.content : '',
        'Accept': 'application/json'
      }
    })
    .then(response => {
      if (response.ok) {
        return response.json()
      } else {
        return response.json().then(err => Promise.reject(err))
      }
    })
    .then(data => {
      // Hide "No notes yet" message if it exists
      const noNotesMessage = this.containerTarget.parentElement.querySelector('p.text-gray-500.text-sm')
      if (noNotesMessage && noNotesMessage.textContent.includes('No notes yet')) {
        noNotesMessage.remove()
      }

      // Create new note element
      const noteDiv = document.createElement('div')
      noteDiv.className = 'border-l-4 border-blue-500 pl-4 py-2'

      const createdDate = new Date(data.created_at)
      const timeAgo = this._getTimeAgo(createdDate)
      const formattedDate = this._formatDate(createdDate)

      noteDiv.innerHTML = `
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <p class="text-sm text-gray-900">${this._escapeHtml(data.note)}</p>
            <p class="text-xs text-gray-500 mt-1">
              ${this._escapeHtml(data.created_by_name)} • ${timeAgo}
            </p>
          </div>
          <span class="text-xs text-gray-400">${formattedDate}</span>
        </div>
      `

      // Insert at the beginning of notes container
      if (this.containerTarget.firstChild) {
        this.containerTarget.insertBefore(noteDiv, this.containerTarget.firstChild)
      } else {
        this.containerTarget.appendChild(noteDiv)
      }

      // Clear form
      this.textareaTarget.value = ''

      // Re-enable submit button
      this.submitTarget.disabled = false
      this.submitTarget.value = originalButtonText
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Failed to add note. Please try again.')

      // Re-enable submit button
      this.submitTarget.disabled = false
      this.submitTarget.value = originalButtonText
    })
  }

  _escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  _getTimeAgo(date) {
    const seconds = Math.floor((new Date() - date) / 1000)
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    }

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit)
      if (interval >= 1) {
        return interval + ' ' + unit + (interval > 1 ? 's' : '') + ' ago'
      }
    }
    return 'just now'
  }

  _formatDate(date) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = months[date.getMonth()]
    const day = date.getDate()
    const year = date.getFullYear()
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12
    hours = hours ? hours : 12
    const minutesStr = minutes < 10 ? '0' + minutes : minutes
    return `${month} ${day}, ${year} ${hours}:${minutesStr} ${ampm}`
  }
}
